import React, { useState, useCallback, useEffect } from 'react';
import log from 'shared/utils/log';
import dayjs from 'dayjs';
import { format, formatDistanceStrict, formatDistanceToNowStrict } from 'date-fns';
import { Icon } from 'shared/components/Icon';
import UserAvatar from 'shared/components/UserAvatar';
import { message } from 'antd';
import { VisitorActivity, VisitorActivityType, VisitorTopPage } from 'sb-lib/types';
import { getVisitorTopPages } from 'shared/api';
import { ActiveVisitor } from 'types';
import './style.less';
import { VisitorOverviewActivity } from './Activity';
import Skeleton from 'react-loading-skeleton';
import { SpotlightUtms } from 'browser/components/Spotlight/Account/Utm';
import { SpotlightSegments } from 'browser/components/Spotlight/Account/Segments';
import { VisitorOverviewAccount } from './Account';

export const DATE_GROUPS = [
  {
    label: 'Today',
    endTime: dayjs().endOf('day').valueOf(),
    startTime: dayjs().startOf('day').valueOf(),
  },
  {
    label: 'Yesterday',
    endTime: dayjs().endOf('day').subtract(1, 'day').valueOf(),
    startTime: dayjs().startOf('day').subtract(1, 'day').valueOf(),
  },
  {
    label: 'Earlier this week',
    endTime: dayjs().endOf('day').subtract(2, 'day').valueOf(),
    startTime: dayjs().startOf('week').valueOf(),
  },
  {
    label: 'Last week',
    endTime: dayjs().endOf('week').subtract(1, 'week').valueOf(),
    startTime: dayjs().startOf('week').subtract(1, 'week').valueOf(),
  },
  // Not including "Earlier this month" & "Last Month" as they can potentially
  // conflict with the existing date groupings
  {
    label: 'Older',
    endTime: dayjs().endOf('week').subtract(2, 'week').valueOf(),
    startTime: undefined,
  },
];

interface VisitorOverviewProps {
  visitor: ActiveVisitor | null;
}

export const VisitorOverview: React.FC<VisitorOverviewProps> = ({ visitor }) => {
  const [topPages, setTopPages] = useState<VisitorTopPage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const visitorId = visitor?.id;

  const getTopPages = useCallback(async () => {
    if (!visitorId) return;
    setLoading(true);

    try {
      const response = await getVisitorTopPages(visitorId);
      setTopPages(response.data);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      log.error("Error retrieving Visitor's top pages");
      message.error(
        'There was an issue retrieving the Top Pages, support has been notified.',
      );
    }
  }, [visitorId]);

  useEffect(() => {
    getTopPages();
  }, [getTopPages]);

  return (
    <div className="VisitorOverview">
      <div className="VisitorOverview__main">
        <div className="VisitorOverview__content">
          <div className="VisitorOverview__topPages">
            <div className="VisitorOverview__topPagesHeader">
              <Icon icon="globe" width={20} height={20} />
              Top pages visited
            </div>
            <div className="VisitorOverview__topPagesSection">
              {!loading &&
                topPages &&
                topPages.map(page => (
                  <div key={page.url} className="VisitorOverview__topPagesItem">
                    <div className="VisitorOverview__topPagesPage">
                      <Icon icon="linkExternal" />
                      <span>{page.url}</span>
                    </div>
                    <div className="VisitorOverview__topPagesVisits">
                      {page.visits} visit{page.visits === 1 ? '' : 's'}
                    </div>
                  </div>
                ))}
              {loading &&
                [...new Array(3)].map((_, i) => (
                  <div key={i} className="VisitorOverview__topPagesItem">
                    <div className="VisitorOverview__topPagesPage">
                      <Icon icon="linkExternal" />
                      <span>
                        <Skeleton baseColor="#F6F7F9" height={'100%'} width={200} />
                      </span>
                    </div>
                    <div className="VisitorOverview__topPagesVisits">
                      <Skeleton baseColor="#F6F7F9" height={'100%'} width={30} />
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <VisitorOverviewActivity visitorId={visitor?.id} />
        </div>
        <div className="VisitorOverview__sidebar">
          {!!visitor?.company?.id && (
            <VisitorOverviewAccount accountId={visitor?.company?.id} />
          )}
          <SpotlightUtms contact={visitor?.contact} />
          <SpotlightSegments contact={visitor?.contact} />
        </div>
      </div>
    </div>
  );
};

interface VisitorOverviewItemProps {
  activity: VisitorActivity;
  activities: VisitorActivity[];
  visitor: ActiveVisitor | null;
}

export const VisitorOverviewItem: React.FC<VisitorOverviewItemProps> = ({
  activity,
  activities,
  visitor,
}) => {
  switch (activity.type) {
    // TODO: None of the commented out Overview Items here have corresponding "Activity" types in the backend
    // TODO: Uncomment and correct them as new "Activity" types become available
    // TODO: If adding new Activities, be sure to add the correct Activity "type" filter to the Visitor model
    // TODO: method `get_session_activities_query`

    // case 'ASSIGNED':
    //   return (
    //     <div className="VisitorOverviewItem">
    //       <div className="VisitorOverviewItem__icon">
    //         <Icon icon="restart" className="restart" />
    //       </div>
    //       <div className="VisitorOverviewItem__content">
    //         <div className="VisitorOverviewItem__summary">
    //           <span className="VisitorOverviewItem__summary-action">Assigned to:</span>
    //           &nbsp;Was {item.from} <Icon icon="back" className="back" /> {item.to}{' '}
    //           {formatDistanceToNowStrict(item.timestamp, { addSuffix: true })}&nbsp; by{' '}
    //           {item.by}
    //         </div>
    //       </div>
    //     </div>
    //   );
    //
    // case 'EMAIL_UPDATE':
    //   return (
    //     <div className="VisitorOverviewItem">
    //       <div className="VisitorOverviewItem__icon">
    //         <Icon icon="restart" className="restart" />
    //       </div>
    //       <div className="VisitorOverviewItem__content">
    //         <div className="VisitorOverviewItem__summary">
    //           <span className="VisitorOverviewItem__summary-action">Email updated:</span>
    //           &nbsp;{item.to}&nbsp;
    //           {formatDistanceToNowStrict(item.timestamp, { addSuffix: true })}
    //         </div>
    //       </div>
    //     </div>
    //   );
    //
    // case 'JOURNEY':
    //   return (
    //     <div className="VisitorOverviewItem">
    //       <div className="VisitorOverviewItem__icon">
    //         <Icon icon="branch" className="branch" />
    //       </div>
    //       <div className="VisitorOverviewItem__content">
    //         <div className="VisitorOverviewItem__summary">
    //           <span className="VisitorOverviewItem__summary-action">
    //             Triggered journey:
    //           </span>
    //           &nbsp;<u>{item.name}</u>&nbsp;
    //           {formatDistanceToNowStrict(item.timestamp, { addSuffix: true })}
    //         </div>
    //         <div className="VisitorOverviewItem__card">
    //           <div className="VisitorOverviewItem__card-icon">
    //             <Icon icon="reply" className="reply" />
    //           </div>
    //           <div className="VisitorOverviewItem__card-content">
    //             <div className="VisitorOverviewItem__card-message">
    //               {item.card?.message}
    //             </div>
    //             <div className="VisitorOverviewItem__card-data">
    //               {item.card?.timestamp &&
    //                 formatDistanceToNowStrict(item.card?.timestamp, { addSuffix: true })}
    //               &nbsp;&nbsp;·&nbsp;&nbsp;
    //               {item.card?.from}
    //             </div>
    //           </div>
    //         </div>
    //       </div>
    //     </div>
    //   );

    case 'TAKEOVER':
      return (
        <div className="VisitorOverviewItem">
          <div className="VisitorOverviewItem__icon 'VisitorOverviewItem__icon--active">
            <Icon icon="pointer" className="pointer" />
          </div>
          <div className="VisitorOverviewItem__content">
            <div className="VisitorOverviewItem__summary">
              <div className="VisitorOverviewItem__summary-avatar">
                {activity.user && <UserAvatar user={activity.user} size={32} />}
              </div>
              &nbsp;
              <span className="VisitorOverviewItem__summary-action">
                {activity.user?.displayName}
              </span>
              &nbsp;Started a screen takeover&nbsp;
              {formatDistanceToNowStrict(activity.startTime, { addSuffix: true })}
            </div>
          </div>
        </div>
      );

    case 'REJECTED_CALL':
      return (
        <div className="VisitorOverviewItem">
          <div className="VisitorOverviewItem__icon">
            <Icon icon="camera" className="camera" />
          </div>
          <div className="VisitorOverviewItem__content">
            <div className="VisitorOverviewItem__summary">
              <span className="VisitorOverviewItem__summary-action">
                Rejected a video call
              </span>
              &nbsp;
              {formatDistanceToNowStrict(activity.startTime, { addSuffix: true })}
              &nbsp;&nbsp;·&nbsp;&nbsp;
              {format(activity.startTime, 'p')}
            </div>
          </div>
        </div>
      );

    case 'HELP_REQUEST_MISSED':
      return (
        <div className="VisitorOverviewItem">
          <div className="VisitorOverviewItem__icon">
            <Icon icon="camera" className="camera" />
          </div>
          <div className="VisitorOverviewItem__content">
            <div className="VisitorOverviewItem__summary">
              <span className="VisitorOverviewItem__summary-action">
                Missed video request
              </span>
              &nbsp;
              {formatDistanceToNowStrict(activity.startTime, { addSuffix: true })}
              &nbsp;&nbsp;·&nbsp;&nbsp;
              {format(activity.startTime, 'p')}
            </div>
          </div>
        </div>
      );

    case 'SESSION': {
      // Activities are sorted in time descending, so first array element is most recent
      const sessionPageVisits = activities.filter(
        a =>
          a.type == VisitorActivityType.PAGE_VISIT && a.sessionId == activity.sessionId,
      );
      const firstPage = sessionPageVisits && sessionPageVisits.pop();
      const lastPage = sessionPageVisits && sessionPageVisits.shift();

      return (
        <div className="VisitorOverviewItem">
          <div className="VisitorOverviewItem__icon">
            <Icon icon="eye" className="eye" />
          </div>
          <div className="VisitorOverviewItem__content">
            <div className="VisitorOverviewItem__summary">
              <span className="VisitorOverviewItem__summary-action">
                {visitor?.displayName}
              </span>
              &nbsp; started a new session&nbsp;
              {formatDistanceToNowStrict(activity.startTime, { addSuffix: true })}
            </div>
            <div className="VisitorOverviewItem__card">
              <div className="VisitorOverviewItem__card-content">
                <div className="VisitorOverviewItem__card-message">Browsing history</div>
                <div className="VisitorOverviewItem__card-history">
                  {activity.lastSeenTime && activity.startTime && (
                    <div className="VisitorOverviewItem__card-duration">
                      <Icon icon="clock" className="clock" />
                      {formatDistanceStrict(activity.lastSeenTime, activity.startTime, {
                        addSuffix: true,
                      })}
                    </div>
                  )}
                  {activity.referrer && (
                    <div className="VisitorOverviewItem__card-page">
                      <div className="VisitorOverviewItem__card-ref">
                        <Icon icon="chain" className="chain" />
                      </div>
                      {activity.referrer}
                    </div>
                  )}
                  {firstPage && (
                    <>
                      <Icon icon="back" className="back" />
                      <div className="VisitorOverviewItem__card-page">
                        <div className="VisitorOverviewItem__card-ref">
                          <Icon icon="enter" className="enter" />
                        </div>
                        {firstPage.url || firstPage.data?.url}
                        {!lastPage && (
                          <div className="VisitorOverviewItem__card-ref">
                            <Icon icon="exit" className="exit" />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  {sessionPageVisits && sessionPageVisits.length > 0 && (
                    <>
                      <Icon icon="back" className="back" />
                      <div className="VisitorOverviewItem__card-page">
                        +{sessionPageVisits.length} other page
                        {sessionPageVisits.length > 1 ? 's' : ''}
                      </div>
                    </>
                  )}
                  {lastPage && (
                    <>
                      <Icon icon="back" className="back" />
                      <div className="VisitorOverviewItem__card-page">
                        {lastPage.url || lastPage.data?.url}
                        {activity.lastSeenTime && (
                          <div className="VisitorOverviewItem__card-ref">
                            <Icon icon="exit" className="exit" />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    case 'CALL':
      return (
        <div className="VisitorOverviewItem">
          <div className="VisitorOverviewItem__icon 'VisitorOverviewItem__icon--active">
            <Icon icon="phoneOn" className="pointer" />
          </div>
          <div className="VisitorOverviewItem__content">
            <div className="VisitorOverviewItem__summary">
              <div className="VisitorOverviewItem__summary-avatar">
                {activity.user && <UserAvatar user={activity.user} size={32} />}
              </div>
              &nbsp;
              <span className="VisitorOverviewItem__summary-action">
                {activity.user?.displayName}
              </span>
              &nbsp;called&nbsp;
              <strong>{activity.contact?.name}</strong>&nbsp;
              {formatDistanceToNowStrict(activity.startTime, { addSuffix: true })}
            </div>
          </div>
        </div>
      );

    default:
      return null;
  }
};
